import {
  createColumnHelper,
  getCoreRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import PaginatedTable from '../../Common/PaginatedTable';
import { HiPencilAlt, HiXCircle } from 'react-icons/hi';
import { formatNumbersDisplayOnly } from '../../../Utils';
import { InventoryDailyPrice } from '../../../Models/InventoryDailyPrice';

export default function InventoryDailyPriceListTable({
  data,
  deleteInventory,
  editInventory,
}: {
  data: InventoryDailyPrice[];
  deleteInventory(id: number): void;
  editInventory: (inv: InventoryDailyPrice) => void;
}) {
  const columnHelper = createColumnHelper<InventoryDailyPrice>();

  const columns = [
    columnHelper.accessor((row) => row.id, {
      id: 'id',
      header: '',
      cell: (info) => (
        <div className='flex justify-center gap-1'>
          <button
            type='button'
            className='buttonMain p-2'
            onClick={() => editInventory(info.row.original)}
          >
            <HiPencilAlt />
          </button>
          {/* <button
            type='button'
            className='buttonCancel p-2'
            onClick={() => deleteInventory(info.getValue())}
          >
            <HiXCircle />
          </button> */}
        </div>
      ),
      footer: (info) => info.column.id,
      size: 35,
      enableColumnFilter: false,
      enableSorting: false,
    }),
    columnHelper.accessor((row) => row.currentPrice, {
      id: 'currentPrice',
      header: 'Price',
      sortingFn: 'alphanumeric',
      cell: (info) => (
        <i>${formatNumbersDisplayOnly(info.row.original.currentPrice ?? 0)}</i>
      ),
      footer: (info) => info.column.id,
      filterFn: 'includesString',
      enableColumnFilter: false,
    }),
    columnHelper.accessor((row) => row.dateOfPrice, {
      id: 'dateOfPrice',
      header: 'Date',
      cell: (info) => {
        const dateStr = info.getValue();
        return dateStr ? <i>{dateStr}</i> : <i>N/A</i>;
      },
      sortingFn: (a, b) => {
        const dateA = a.original.dateOfPrice ? new Date(a.original.dateOfPrice).getTime() : 0;
        const dateB = b.original.dateOfPrice ? new Date(b.original.dateOfPrice).getTime() : 0;
        return dateA - dateB; // Ascending - TanStack Table will reverse for desc: true
      },
      footer: (info) => info.column.id,
      filterFn: 'includesString',
      enableColumnFilter: false,
    }),
    columnHelper.accessor((row) => row.inventoryType?.name, {
      id: 'inventoryTypeId',
      header: 'Type',
      sortingFn: 'alphanumeric',
      footer: (info) => info.column.id,
      filterFn: 'includesString',
      enableColumnFilter: false,
    }),
    columnHelper.accessor((row) => row.state?.code, {
      id: 'stateId',
      header: 'State/Province',
      sortingFn: 'alphanumeric',
      footer: (info) => info.column.id,
      filterFn: 'includesString',
      enableColumnFilter: false,
    }),
  ];

  const table = useReactTable({
    data,
    columns,
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 20,
      },
      sorting: [
        {
          id: 'dateOfPrice',
          desc: true,
        },
      ],
    },
    getSortedRowModel: getSortedRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
  });

  return (
    <>
      <div className='w-full mx-auto overflow-x-auto'>
        <PaginatedTable
          table={table}
          dataCount={data.length}
          columnResizeMode='onChange'
          showFilters={true}
        />
      </div>
    </>
  );
}
